import type { Deal } from "@domain/CRM/deal";
import type { IDeals } from "@ports/deals/IDeals";

export interface DuplicateDealsUseCasesDeps {
	dealsApi: IDeals;
	/** Default concurrency for the worker pool. The view supplies its own
	 *  per-call value when needed; the constructor default just sets the fallback. */
	defaultConcurrency?: number;
}

type DuplicateRowStatus = "running" | "success" | "failed";

export interface DuplicateProgressEvent {
	dealId: string;
	status: DuplicateRowStatus;
	/** Set only when `status === "failed"`. */
	error?: string;
}

interface RunDuplicateDealsInput {
	deals: Deal[];
	target: string;
	/** Per-deal flags forwarded to `buildPayload`. */
	optionsFor: (deal: Deal) => { isActive: boolean; isPrimary: boolean };
	/** Builds the wire payload from `(deal, target, options)`. The view supplies
	 *  the existing `buildDuplicatePayload` helper; CLI/tests pass anything that
	 *  produces a `Partial<Deal>`. */
	buildPayload: (deal: Deal, target: string, options: { isActive: boolean; isPrimary: boolean }) => Partial<Deal>;
	/** Called as each row advances through running → success/failed. */
	onProgress: (event: DuplicateProgressEvent) => void;
	/** Number of in-flight `createOne` calls. Defaults to `defaultConcurrency`
	 *  passed at construction; callers can override per-run. */
	concurrency?: number;
}

/**
 * Runs the bulk-duplicate-deals workflow as a fixed-size worker pool. The
 * GUI's DealCreator page lets ops select N source deals and a target
 * season; this use case fans those out across `concurrency` parallel
 * workers, each repeatedly popping one deal off the shared queue, calling
 * `IDeals.createOne(buildPayload(...))`, and reporting progress.
 *
 * Per-deal failures are caught and surfaced through `onProgress` —
 * one worker's failed createOne does not stop the others. The method
 * resolves once every worker has drained the queue, regardless of how
 * many rows succeeded or failed.
 *
 * Pure-ish: no React, no globals; everything that varies between the
 * GUI and a hypothetical CLI driver is supplied per call (the deal list,
 * payload builder, progress callback, target). The default concurrency
 * mirrors the existing constant in DealCreator.vm.ts (3).
 */
export interface DuplicateDealsUseCases {
	run(input: RunDuplicateDealsInput): Promise<void>;
}

async function run(deps: DuplicateDealsUseCasesDeps, input: RunDuplicateDealsInput): Promise<void> {
	if (input.deals.length === 0) return;

	const queue: Deal[] = [...input.deals];
	const concurrency = Math.max(1, input.concurrency ?? deps.defaultConcurrency ?? 3);

	const worker = async (): Promise<void> => {
		while (queue.length > 0) {
			const deal = queue.shift();
			if (!deal) break;
			input.onProgress({ dealId: deal.id, status: "running" });
			try {
				await deps.dealsApi.createOne(input.buildPayload(deal, input.target, input.optionsFor(deal)));
				input.onProgress({ dealId: deal.id, status: "success" });
			} catch (e: unknown) {
				const message = e instanceof Error ? e.message : String(e);
				input.onProgress({ dealId: deal.id, status: "failed", error: message });
			}
		}
	};

	await Promise.all(Array.from({ length: concurrency }, () => worker()));
}

export function makeDuplicateDealsUseCases(deps: DuplicateDealsUseCasesDeps): DuplicateDealsUseCases {
	return {
		run: (input) => run(deps, input),
	};
}
